import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductImageStyles, productImageStyles } from "@store/Overrides/Components/ProductImage";
import { COMMON, TYPOGRAPHY } from "@store/theme/design";
import { contentSpacingStyles, linkStyles, transitionControlStyles } from "@store/theme/mixins";
import { CarouselArrowDirection, dotWrapperStyles, getCarouselArrowProps } from "@store/theme/props/carousel";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import { css } from "styled-components";

export interface StoreProductCarouselProps {
    carouselContainer?: InjectableCss;
    mainWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    partNumberText?: TypographyProps;
    addToCartWrapper?: InjectableCss;
    productPriceWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
}

export const getArrowWrapperProps = (direction: "right" | "left", isAlt: boolean) => ({
    css: css`
        position: absolute;
        top: 0;
        ${direction}: ${isAlt ? "10px" : "0"};
        z-index: 1;
        display: ${isAlt ? "block" : "none"};
        width: 0;
        height: 0;
        transform: translateY(-50%);

        ${props =>
            breakpointValuesMediaQueries(
                props.theme,
                [{ values: [null, `${isAlt ? "0" : "block"}`], properties: [`${isAlt ? direction : "display"}`] }],
                mediaOptions.min,
            )}
    `,
});

export const getArrowProps = (direction: CarouselArrowDirection) => {
    const props = getCarouselArrowProps(direction);

    return {
        ...props,
        css: css`
            ${props?.css}
            transform: translate3d(-50%, -50%, 0);
            transition: ${transitionControlStyles()};

            &:disabled {
                opacity: 0;
                pointer-events: none;
            }
        `,
    };
};

export const productCarouselProps: StoreProductCarouselProps = {
    mainWrapper: {
        css: css`
            position: relative;
            height: 100%;
            width: 100%;
        `,
    },
    innerWrapper: {
        css: css`
            overflow: hidden;
            padding: 0 ${COMMON.spacing.side[0]};
            margin: 0 -${COMMON.spacing.side[0]};

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        css`
                            padding: 0;
                            margin: 0;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    carouselContainer: {
        css: css`
            width: 100%;
        `,
    },
    titleText: {
        variant: "h4",
        forwardAs: "h2",
        size: 20,
        css: css`
            padding-top: 30px;
            margin-bottom: 43px;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            ${dotWrapperStyles()}
            margin-top: 30px;
            color: ${getColor("text")};
        `,
    },
    productImageStyles: {
        ...productImageStyles,
        wrapper: {
            ...productImageStyles.wrapper,
            css: css`
                ${productImageStyles.wrapper?.css}
                background-color: ${getColor("common.background")};
                ${contentSpacingStyles(null, "14px")}
            `,
        },
        image: productImageStyles.image,
    },
    productBrandWrapper: {
        css: css`
            line-height: ${TYPOGRAPHY.lineHeight.small};
            ${contentSpacingStyles(null, "3px")}
        `,
    },
    productBrandStyles: {
        nameLink: {
            typographyProps: {
                weight: TYPOGRAPHY.weight.base,
            },
            css: css`
                ${linkStyles("text", "primary", false)}
            `,
        },
        nameText: {
            weight: TYPOGRAPHY.weight.base,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: TYPOGRAPHY.size.base,
                lineHeight: TYPOGRAPHY.lineHeight.small,
                weight: TYPOGRAPHY.weight.bold,
            },
            css: css`
                width: 100%;
                ${linkStyles("text", "primary", false)}
                ${contentSpacingStyles(null, "6px")}
            `,
        },
    },
    partNumberText: {
        size: TYPOGRAPHY.size.smallest,
        css: css`
            ${contentSpacingStyles(null, "6px")}
        `,
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            ${contentSpacingStyles("12px", null)}
        `,
    },
    productPriceWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
        `,
    },
    productPrice: {
        wrapper: css`
            width: auto;
        `,
        price: {
            priceText: {
                size: TYPOGRAPHY.size.smaller,
                lineHeight: TYPOGRAPHY.lineHeight.smaller,
            },
        },
    },
};
